import { Link, NavLink, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useAuthStore, useThemeStore } from "../stores/StoreContext";
import { signOut } from "firebase/auth";
import { auth, db } from "../config/firebase";
import { useEffect, useState, useRef } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  doc,
  orderBy,
  limit,
} from "firebase/firestore";
import logo from "../../src/trocyo.png";

const Header = observer(() => {
  const authStore  = useAuthStore();
  const themeStore = useThemeStore();
  const { currentUser, userProfile } = authStore;
  const navigate = useNavigate();

  const [notifications, setNotifications]     = useState([]);
  const [unreadCount, setUnreadCount]         = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef   = useRef(null);
  const notificationRef  = useRef(null);

  /* ── Notification listener ── */
  useEffect(() => {
    if (!currentUser) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    const q = query(
      collection(db, "notifications"),
      where("userId", "==", currentUser.uid),
      orderBy("createdAt", "desc"),
      limit(20)
    );
    const unsub = onSnapshot(q, (snap) => {
      const notifs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setNotifications(notifs);
      setUnreadCount(notifs.filter((n) => !n.read).length);
    });
    return () => unsub();
  }, [currentUser]);

  /* ── Click-outside closes dropdowns ── */
  useEffect(() => {
    if (!showNotifications && !showProfileMenu) return;
    const handler = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showNotifications, showProfileMenu]);

  const handleLogout = async () => {
    setShowProfileMenu(false);
    await signOut(auth);
    navigate("/");
  };

  const markAsRead = async (id) => {
    try { await updateDoc(doc(db, "notifications", id), { read: true }); } catch (_) {}
  };

  const markAllAsRead = async () => {
    try {
      await Promise.all(
        notifications.filter((n) => !n.read).map((n) =>
          updateDoc(doc(db, "notifications", n.id), { read: true })
        )
      );
    } catch (_) {}
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
    const diff = Math.floor((Date.now() - d) / 60000);
    if (diff < 1)  return "Just now";
    if (diff < 60) return `${diff}m ago`;
    const h = Math.floor(diff / 60);
    if (h < 24)   return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  const isAuthenticated = Boolean(currentUser);
  const initials = (
    userProfile?.name?.[0] ||
    currentUser?.displayName?.[0] ||
    currentUser?.email?.[0] ||
    "U"
  ).toUpperCase();

  return (
    <header className="header">
      <div className="header-container">
        {/* Brand */}
        <Link to="/" className="brand">
          <img src={logo} alt="TROCYO" className="brand-logo" />
        </Link>

        {/* Public nav links (always visible) */}
        <nav className="header-nav" style={{ flex: 1, paddingLeft: 16 }}>
          <NavLink to="/search"           className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}>Rooms</NavLink>
          <NavLink to="/requirements"     className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}>Requirements</NavLink>
          <NavLink to="/travel-companion" className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}>Travel</NavLink>
        </nav>

        {/* Right-side controls */}
        <div className="header-nav">
          {/* Theme toggle */}
          <button
            type="button"
            className="theme-toggle"
            onClick={() => themeStore.toggleTheme()}
            title={themeStore.isDark ? "Switch to light mode" : "Switch to dark mode"}
            aria-label="Toggle theme"
          >
            {themeStore.isDark ? "☀️" : "🌙"}
          </button>

          {!isAuthenticated ? (
            <>
              <Link to="/login"  className="nav-btn">Login</Link>
              <Link to="/signup" className="nav-btn primary">Sign Up</Link>
            </>
          ) : (
            <>
              {/* Notification bell */}
              <div className="notification-container" ref={notificationRef}>
                <button
                  type="button"
                  className="notification-btn"
                  onClick={() => setShowNotifications((p) => !p)}
                  aria-label="Notifications"
                >
                  🔔
                  {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount}</span>
                  )}
                </button>

                {showNotifications && (
                  <div className="notification-dropdown">
                    <div className="notification-header">
                      <h4>Notifications</h4>
                      {unreadCount > 0 && (
                        <button className="mark-all-read" onClick={markAllAsRead}>
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="notification-list">
                      {notifications.length > 0 ? (
                        notifications.map((n) => {
                          const tabMap = {
                            room_request:        "roomPosts",
                            requirement_request: "postedRequirements",
                            travel_request:      "myJourney",
                          };
                          const tab = tabMap[n.type];
                          return (
                            <div
                              key={n.id}
                              className={`notification-item ${n.read ? "read" : "unread"}`}
                              onClick={() => !n.read && markAsRead(n.id)}
                            >
                              <div className="notification-title">{n.title}</div>
                              <div className="notification-message">{n.message}</div>
                              <div className="notification-item-footer">
                                <div className="notification-time">{formatTime(n.createdAt)}</div>
                                {tab && (
                                  <button
                                    className="notification-view-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      markAsRead(n.id);
                                      setShowNotifications(false);
                                      navigate("/activity", { state: { activeTab: tab } });
                                    }}
                                  >
                                    View →
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="notification-empty">No notifications yet</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Profile menu */}
              <div className="profile-menu-container" ref={profileMenuRef}>
                <button
                  type="button"
                  className="profile-menu-trigger"
                  onClick={() => setShowProfileMenu((p) => !p)}
                  aria-haspopup="true"
                  aria-expanded={showProfileMenu}
                >
                  <span className="profile-menu-initial">{initials}</span>
                </button>

                {showProfileMenu && (
                  <div className="profile-menu-dropdown">
                    <div className="profile-menu-header">
                      <div className="profile-menu-avatar">{initials}</div>
                      <div className="profile-menu-info">
                        <div className="profile-menu-name truncate">
                          {userProfile?.name || currentUser?.displayName || "User"}
                        </div>
                        <div className="profile-menu-email truncate">
                          {userProfile?.email || currentUser?.email}
                        </div>
                      </div>
                    </div>

                    <div className="profile-menu-divider" />

                    <button
                      type="button"
                      className="profile-menu-item"
                      onClick={() => { setShowProfileMenu(false); navigate("/post-room"); }}
                    >
                      <span className="menu-item-icon">🏠</span>
                      <span>Post a Room</span>
                    </button>
                    <button
                      type="button"
                      className="profile-menu-item"
                      onClick={() => { setShowProfileMenu(false); navigate("/submit-requirement"); }}
                    >
                      <span className="menu-item-icon">📋</span>
                      <span>Submit Requirement</span>
                    </button>
                    <button
                      type="button"
                      className="profile-menu-item"
                      onClick={() => { setShowProfileMenu(false); navigate("/lookout"); }}
                    >
                      <span className="menu-item-icon">👥</span>
                      <span>Lookout</span>
                    </button>

                    <div className="profile-menu-divider" />

                    <button
                      type="button"
                      className="profile-menu-item"
                      onClick={() => { setShowProfileMenu(false); navigate("/profile"); }}
                    >
                      <span className="menu-item-icon">👤</span>
                      <span>My Profile</span>
                    </button>
                    <button
                      type="button"
                      className="profile-menu-item"
                      onClick={() => { setShowProfileMenu(false); navigate("/activity"); }}
                    >
                      <span className="menu-item-icon">📊</span>
                      <span>My Activity</span>
                    </button>

                    <div className="profile-menu-divider" />

                    <button
                      type="button"
                      className="profile-menu-item destructive"
                      onClick={handleLogout}
                    >
                      <span className="menu-item-icon">→</span>
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
});

export default Header;
